import { useState } from 'react';
import { Button, Card, CardBody, ConfirmDialog, Eyebrow } from '@/shared/components';
import { I } from '@/shared/icons';
import type { InvoiceItemPresetResponse } from '@/shared/api';
import { InvoiceItemPresetDialog } from './InvoiceItemPresetDialog';
import { PresetSortableList } from './PresetSortableList';
import { useInvoiceItemPresets } from './useInvoiceItemPresets';

/**
 * Der Positionskatalog als zweiter Abschnitt der Rechnungsvorlagen.
 *
 * Bewusst hier und nicht am Apartment: die Auswahl gilt für den ganzen Workspace. Die
 * apartment-gebundenen *wiederkehrenden Posten* sind etwas anderes — sie hängen automatisch an
 * jeder Buchungsrechnung, diese Positionen werden von Hand gewählt.
 */
export function InvoiceItemPresetsCard() {
  const catalogue = useInvoiceItemPresets();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<InvoiceItemPresetResponse | null>(null);
  const [deleting, setDeleting] = useState<InvoiceItemPresetResponse | null>(null);

  const openEditor = (preset: InvoiceItemPresetResponse | null) => {
    setEditing(preset);
    setDialogOpen(true);
  };

  return (
    <Card>
      <CardBody className="p-6">
        <header>
          <Eyebrow variant="muted">Konfiguration</Eyebrow>
          <h3 className="text-ink mt-1 font-serif text-xl tracking-tight">
            Positions-<span className="font-serif-italic">Katalog</span>
            <span className="text-sealwax">.</span>
          </h3>
          <p className="text-ink-soft mt-1 text-sm">
            Die Auswahl, aus der eine Rechnung von Hand zusammengestellt wird. Preis und Bezeichnung
            lassen sich je Rechnung überschreiben.
          </p>
        </header>

        <div className="mt-6">
          {catalogue.isLoading ? (
            <p className="text-ink-soft text-sm">Katalog wird geladen…</p>
          ) : catalogue.hasError ? (
            <p role="alert" className="text-sealwax text-sm">
              Katalog konnte nicht geladen werden.
            </p>
          ) : catalogue.presets.length === 0 ? (
            <div className="border-line text-ink-soft rounded-md border border-dashed py-8 text-center text-sm">
              Noch keine Positionen. Typisch sind Parkplatz, Reinigung, Kurtaxe oder Hundegebühr.
            </div>
          ) : (
            <PresetSortableList
              presets={catalogue.presets}
              onReorder={catalogue.applyOrder}
              onEdit={openEditor}
              onDelete={setDeleting}
            />
          )}

          <div className="mt-4">
            <Button variant="ghost" className="min-h-11" onClick={() => openEditor(null)}>
              <I name="Plus" size={14} aria-hidden />
              Position hinzufügen
            </Button>
          </div>
        </div>
      </CardBody>

      <InvoiceItemPresetDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditing(null);
        }}
        preset={editing}
        saving={catalogue.saving}
        onSubmit={(values) => {
          // Nur bei Erfolg schliessen: bei einer doppelten Bezeichnung soll der Dialog mit den
          // eingegebenen Werten stehenbleiben, statt sie wegzuwerfen.
          const close = () => {
            setDialogOpen(false);
            setEditing(null);
          };
          if (editing) {
            catalogue.update.mutate({ id: editing.id, body: values }, { onSuccess: close });
          } else {
            catalogue.create.mutate(values, { onSuccess: close });
          }
        }}
      />

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        title="Position entfernen?"
        description={
          deleting
            ? `„${deleting.label}" verschwindet aus der Auswahl. Bereits ausgestellte Rechnungen bleiben unverändert — sie tragen ihre Positionen selbst.`
            : ''
        }
        confirmLabel="Entfernen"
        loading={catalogue.removing}
        onConfirm={() => {
          if (deleting) {
            catalogue.remove.mutate(deleting.id, { onSettled: () => setDeleting(null) });
          }
        }}
      />
    </Card>
  );
}
